<template>
	<v-defaults-provider :defaults="overlayDefaults">
		<draggable draggable=".draggable" :group="{name: 'time-box', put: true, pull: false}" class="h-100">
			<v-card
				elevation="0"
				class="d-flex flex-column justify-center h-100"
				:class="[panel.xl ? 'px-4' : panel.lg ? 'px-2' : 'px-0']"
			>
				<!-- select station / operation mode -->
				<v-container class="pa-0">
					<div class="align-center" :class="{'d-flex': panel.xl}">
						<v-col :cols="panel.xl ? 6 : 12" :class="panel.xl ? 'pb-3' : 'pb-2'">
							<v-tooltip
								location="bottom"
								:disabled="!stationConnectedToStudio"
								text="Release before switching stations."
							>
								<template #activator="{props}">
									<div v-bind="props">
										<StationSelector :main-selector="true" />
									</div>
								</template>
							</v-tooltip>
						</v-col>
						<v-col
							:cols="panel.xl ? 5 : 12"
							class="justify-start ms-auto"
							:class="[panel.xl ? 'd-flex align-center pt-3' : 'pt-2']"
						>
							<v-tooltip v-if="connectionErrorFormatted.length > 0" location="bottom">
								<template #activator="{props}">
									<v-icon v-bind="props" id="alert-icon" class="mr-2" color="orange">
										mdi-alert
									</v-icon>
								</template>
								<div v-for="error in connectionErrorFormatted" :key="error">
									<span>{{ error }}</span>
								</div>
							</v-tooltip>
							<v-tooltip location="bottom" :disabled="allPeers.length === 0">
								<template #activator="{props}">
									<v-chip
										v-bind="props"
										class="status"
										color="white"
										:class="{
											'studio-connected':
												(sequencerIsLive ||
													isSyndicationOriginated ||
													isSplitMasterGoLiveDisabled) &&
												!sequencerWaitingForEndOfSyndication &&
												userIsHost &&
												!userConnectionError,
											warning:
												sequencerIsLive && userIsHost && !userConnectionError && !playerIsLive,
											error: sequencerIsLive && userIsHost && userConnectionError,
											'transition-production-mode': sequencerWaitingForEndOfShow
										}"
									>
										{{ connectionStatus }}
									</v-chip>
								</template>
								<div v-for="peer in allPeersFormatted" :key="peer">
									<span>{{ peer }}</span>
								</div>
							</v-tooltip>
						</v-col>
					</div>

					<!-- scheduled show title / studio name & control buttons -->
					<div class="align-center" :class="{'d-flex': panel.xl}">
						<v-col v-if="panel.xl" cols="6" class="d-flex">
							<v-tooltip :disabled="!currentShow">
								<template #activator="{props}">
									<div
										v-bind="props"
										class="show-name text-uppercase"
										:class="[loadingShow ? 'text-disabled font-weight-thin' : 'font-weight-bold']"
									>
										<v-progress-circular v-if="loadingShow" indeterminate size="16" width="1" />
										{{
											currentShow
												? currentShow.name
												: loadingShow
													? "Loading show..."
													: splitMasterStation
														? `Split from Master: ${splitMasterStation.name}`
														: "No scheduled show"
										}}
									</div>
								</template>
								<span v-if="currentShow">{{ currentShow.name }}</span>
							</v-tooltip>
						</v-col>
						<v-col :cols="panel.xl ? 5 : 12" class="ms-auto" :class="panel.xl ? 'pt-3' : 'pt-2'">
							<div class="text-body-1 text-truncate text-uppercase d-flex justify-start">
								<v-tooltip v-if="missingDevices.length > 0" location="bottom">
									<template #activator="{props}">
										<v-icon v-bind="props" id="alert-icon" class="mr-2" color="orange">
											mdi-alert
										</v-icon>
									</template>
									<div v-for="device in missingDevices" :key="device">
										<span
											>The audio device <b>{{ device }}</b> is not available</span
										>
									</div>
								</v-tooltip>
								{{ studioSettings ? studioSettings.name : "No Studio" }}
							</div>
							<div class="d-flex justify-start">
								<v-tooltip
									location="bottom"
									class="pl-3"
									:disabled="
										!disableButton &&
										(userAnyRole || systemInputDevices.length > 0) &&
										(controlStationFeature || (connectedHost && participateStationFeature)) &&
										allPeers.length === 0
									"
								>
									<template #activator="{props}">
										<div v-bind="props">
											<div v-if="!userAnyRole && !connectedHost">
												<!--if sequencer is live, call studioConnect-->
												<v-btn
													id="controlActivatorBtn"
													variant="outlined"
													:loading="studioConnecting"
													:disabled="
														disableButton ||
														!systemInputDevices.length ||
														!controlStationFeature
													"
													@click="
														(!studioDefaultLiveOptionIsChooseEveryTime ||
															sequencerIsLive ||
															isSyndicationOriginated ||
															isSplitMasterGoLiveDisabled) &&
														studioConnect(
															ConferenceRoles.Host,
															studioSettings.preferences.default_live_option
														)
													"
												>
													<v-icon class="pr-2">mdi-antenna</v-icon>
													Control
												</v-btn>

												<!-- The overlay appears on click "Control" button and Sequencer is not Live -->
												<v-overlay
													v-if="
														!sequencerIsLive &&
														!isSyndicationOriginated &&
														!isSplitMasterGoLiveDisabled &&
														studioDefaultLiveOptionIsChooseEveryTime
													"
													activator="#controlActivatorBtn"
													location-strategy="connected"
													close-on-content-click
												>
													<v-card class="confirmation-card">
														<v-card-title class="text-h3 text-center">
															How would you like to take control?
														</v-card-title>
														<v-card-item class="d-flex justify-space-around">
															<v-btn
																class="mr-1"
																variant="outlined"
																color="primary"
																@click="
																	studioConnect(
																		ConferenceRoles.Host,
																		TransitionType.NextEvent.value
																	)
																"
															>
																On next transition
															</v-btn>
															<v-btn
																class="ml-1"
																variant="outlined"
																color="secondary"
																@click="
																	studioConnect(
																		ConferenceRoles.Host,
																		TransitionType.Immediately.value
																	)
																"
															>
																Immediately
															</v-btn>
														</v-card-item>
														<v-card-actions class="pt-0">
															<v-spacer></v-spacer>
															<v-btn color="error" variant="text"> Cancel </v-btn>
														</v-card-actions>
													</v-card>
												</v-overlay>
											</div>

											<div v-else-if="!userAnyRole && connectedHost">
												<v-menu :disabled="!studioIsAlreadyConnectedAsHost" location="bottom">
													<template #activator="{props}">
														<v-btn
															variant="outlined"
															v-bind="studioIsAlreadyConnectedAsHost ? props : {}"
															:loading="studioConnecting"
															:disabled="
																disableButton ||
																!systemInputDevices.length ||
																!participateStationFeature
															"
															@click="
																!studioIsAlreadyConnectedAsHost &&
																studioConnect(ConferenceRoles.Participant)
															"
														>
															<v-icon class="pr-2">mdi-headset</v-icon>
															Participate
														</v-btn>
													</template>
													<v-card class="confirmation-card">
														<v-card-text class="text-caption">
															This studio is already connected to this station as a Host
															from another window. <br />
															Do you want to take over and disconnect the other session?
														</v-card-text>
														<v-card-actions class="pt-0">
															<v-btn
																color="secondary"
																variant="text"
																@click="takeOverStudioConnection()"
															>
																Take over
															</v-btn>
															<v-spacer />
															<v-btn color="error" variant="text">Cancel </v-btn>
														</v-card-actions>
													</v-card>
												</v-menu>
											</div>

											<div
												v-else-if="
													(sequencerIsLive ||
														sequencerWaitingForEndOfShow ||
														isSyndicationOriginated ||
														isSplitMasterGoLiveDisabled) &&
													userIsHost
												"
											>
												<v-btn
													id="releaseActivatorBtn"
													:color="
														waitingForTransition || sequencerWaitingForEndOfShow
															? '#f7ce03'
															: 'secondary'
													"
													:loading="studioDisconnecting"
													:disabled="disableButton"
													@click="
														!(
															waitingForTransition ||
															sequencerWaitingForEndOfShow ||
															nonGuestParticipantPeers.length > 0
														) && studioDisconnect()
													"
												>
													<v-icon class="pr-2">mdi-antenna</v-icon>
													Release
												</v-btn>

												<v-btn
													v-if="guestLinkFeatureIsEnabled"
													icon
													variant="text"
													class="ml-2"
													@click="guestLinkDialog = true"
												>
													<v-icon size="small">mdi-account-multiple-plus</v-icon>
												</v-btn>

												<v-overlay
													v-if="waitingForTransition || sequencerWaitingForEndOfShow"
													activator="#releaseActivatorBtn"
													location-strategy="connected"
													close-on-content-click
												>
													<v-card class="confirmation-card">
														<v-card-text class="text-caption">
															Cloud and Edge players are transitioning to getting the live
															feed. <br />
															Releasing at this state is a risk of bad listening
															experience. <br />
															We suggest that you wait few seconds until the button turns
															Blue.
														</v-card-text>
														<v-card-actions class="pt-0">
															<v-btn
																color="secondary"
																variant="text"
																@click="studioDisconnect()"
															>
																Release
															</v-btn>
															<v-spacer />
															<v-btn color="error" variant="text"> Cancel </v-btn>
														</v-card-actions>
													</v-card>
												</v-overlay>
												<v-overlay
													v-else-if="nonGuestParticipantPeers.length > 0"
													activator="#releaseActivatorBtn"
													location-strategy="connected"
												>
													<TransferHostConfirmation
														:message="transferHostMessage"
														:participants="participantPeers"
														@agree="transferHost"
														@cancel="studioDisconnect"
													/>
												</v-overlay>
											</div>

											<v-btn
												v-else-if="
													(!sequencerIsLive && userAnyRole) ||
													(sequencerIsLive && (userIsParticipant || userIsGuest))
												"
												color="secondary"
												:disabled="disableButton"
												@click="studioDisconnect()"
											>
												<v-icon class="pr-2">
													{{ userIsParticipant ? "mdi-headset" : "mdi-headphones" }}
												</v-icon>
												Disconnect
											</v-btn>

											<v-overlay
												v-model="transferHostOverlay"
												class="align-center justify-center"
												persistent
											>
												<GeneralConfirmation
													message="The host is assigning control to you. Click the 'Reconnect' button to claim it.</br>(Make sure you are not using your Mic at that time)"
													:button-options="{
														agreeBtn: {
															enabled: true,
															message: 'Reconnect'
														},
														cancelBtn: {
															enabled: true,
															message: 'Reject and leave'
														}
													}"
													@agree="acceptTransferHost"
													@cancel="studioDisconnect"
												/>
											</v-overlay>

											<!-- The overlay appears during the connecting Studio process -->
											<v-menu
												v-model="studioConnectStatus.open"
												target="parent"
												location-strategy="connected"
											>
												<v-card>
													<v-card-title
														v-if="studioConnectStatus.transitionType"
														class="text-h3 text-center"
													>
														You have selected to control
														{{ studioConnectStatus.transitionType }}
													</v-card-title>
													<v-card-title v-else class="text-h3 text-center">
														You have selected to monitor
													</v-card-title>
													<v-card-item>
														<v-stepper :model-value="studioConnectCurrentSteps">
															<v-stepper-header
																v-for="(item, i) in studioConnectStatus.steps"
																:key="i"
																class="text-body-2"
															>
																<v-stepper-item
																	v-if="item.visible"
																	:title="item.title"
																	:complete="item.status === 2"
																	:error="item.status === -1"
																	:color="item.status === 2 ? 'success' : undefined"
																	class="pa-3"
																/>
															</v-stepper-header>
														</v-stepper>
													</v-card-item>
													<v-card-actions class="pt-0 justify-end">
														<v-btn color="error" variant="text">Close</v-btn>
													</v-card-actions>
												</v-card>
											</v-menu>
										</div>
									</template>
									<span v-if="!selectedStation"> Disabled when no Station is selected. </span>
									<span v-else-if="!sequencer"> Waiting for Sequencer response. </span>
									<span v-else-if="!playersSnapshotsFetched"> Waiting for Live Players. </span>
									<span v-else-if="!studioSettings"> No Studio Settings available. </span>
									<span v-else-if="!webRTCTokenFetched"> Waiting for token. </span>
									<span v-else-if="!studioSettings.sip_connection_address?.length">
										No SIP connection address under the Studio Settings available.
									</span>
									<span v-else-if="!userAnyRole && !systemInputDevices.length">
										No audio input devices available.
									</span>
									<span
										v-else-if="
											!userAnyRole &&
											(!controlStationFeature || (connectedHost && !participateStationFeature))
										"
									>
										No required permissions for this action.
									</span>
									<div v-for="peer in allPeersFormatted" :key="peer">
										<span>{{ peer }}</span>
									</div>
								</v-tooltip>
								<v-tooltip
									v-if="!userAnyRole"
									location="bottom"
									:disabled="!disableButton && monitorStationFeature && allPeers.length === 0"
								>
									<template #activator="{props}">
										<div v-bind="props" class="ml-2">
											<v-btn
												variant="outlined"
												min-width="52"
												max-width="52"
												:loading="studioConnecting"
												:disabled="disableButton || !monitorStationFeature"
												@click="studioConnect(ConferenceRoles.Guest)"
											>
												<v-icon>mdi-headphones</v-icon>
											</v-btn>
										</div>
									</template>
									<span v-if="!selectedStation"> Disabled when no Station is selected. </span>
									<span v-else-if="!sequencer"> Waiting for Sequencer response. </span>
									<span v-else-if="!playersSnapshotsFetched"> Waiting for Live Players. </span>
									<span v-else-if="!studioSettings"> No Studio Settings available. </span>
									<span v-else-if="!webRTCTokenFetched"> Waiting for token. </span>
									<span v-else-if="!studioSettings.sip_connection_address?.length">
										No SIP connection address under the Studio Settings available.
									</span>
									<span v-else-if="!monitorStationFeature">
										No required permissions for this action.
									</span>
									<div v-for="peer in allPeersFormatted" :key="peer">
										<span>{{ peer }}</span>
									</div>
								</v-tooltip>
							</div>
						</v-col>
					</div>

					<v-divider></v-divider>

					<!-- current time/ timer -->
					<div class="align-center" :class="{'d-flex': panel.xl}">
						<v-col :cols="panel.xl ? 6 : 12" class="d-flex flex-column">
							<StationDateTime
								:date-time="currentTime"
								:format="'dddd MMMM DD, YYYY'"
								:class="'text-subtitle-1 text-truncate text-uppercase current-day'"
							/>
							<StationDateTime
								:date-time="currentTime"
								:format="'HH:mm:ss'"
								:auto-update="true"
								:class="'text-h1'"
							/>
						</v-col>
						<v-col v-if="panel.xl" cols="6" class="d-flex flex-column justify-end">
							<Timer />
						</v-col>
					</div>
				</v-container>
			</v-card>
		</draggable>

		<GuestLinkDialog v-if="guestLinkFeatureIsEnabled" v-model="guestLinkDialog" />

		<v-dialog v-model="microphoneAccessDialog" width="400">
			<v-card color="white">
				<v-card-text class="text-body-2">
					To allow access to Microphone, click on the lock icon on the address bar and select permission for
					Microphone.
				</v-card-text>

				<v-img height="350" src="@/assets/images/Enable_Mic_Permission.png" />

				<v-card-actions>
					<v-spacer />
					<v-btn color="error" @click="microphoneAccessDialog = false"> Close </v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>
	</v-defaults-provider>
</template>

<script>
import config from "@/config";
import emitter from "@/emitter";
/* Libraries */
import * as _ from "lodash";
import moment from "moment-timezone";
import {VueDraggableNext} from "vue-draggable-next";

/* Modules */
import GeneralConfirmation from "@/components/dialogs/GeneralConfirmation.vue";
import GuestLinkDialog from "@/components/dialogs/GuestLinkDialog.vue";
import StationDateTime from "@/components/StationDateTime.vue";
import StationSelector from "@/components/StationSelector.vue";
import Timer from "@/components/Timer.vue";
import TransferHostConfirmation from "@/components/dialogs/TransferHostConfirmation.vue";

/* Enums */
import ConferenceRoles from "@/enums/ConferenceRoles";
import ConfirmationType from "@/enums/ConfirmationType";
import EventStatus from "@/enums/EventStatus";
import EventTypes from "@/enums/EventTypes";
import PriorityLanes from "@/enums/PriorityLanes";
import SequencerModes from "@/enums/SequencerModes";
import StartLiveStatus from "@/enums/StartLiveStatus";
import SyndicationTypes from "@/enums/SyndicationTypes";
import TransitionType from "@/enums/TransitionType";

/* Mixins */
import DurationFormatMixin from "@/mixins/DurationFormatMixin";
import PanelPropertiesMixin from "@/mixins/PanelProperties";
import SequencerPlayerStudioStatusMixin from "@/mixins/SequencerPlayerStudioStatus";

export default {
	name: "TimeBox",

	components: {
		GeneralConfirmation,
		GuestLinkDialog,
		StationDateTime,
		StationSelector,
		TransferHostConfirmation,
		Timer,
		draggable: VueDraggableNext
	},

	mixins: [DurationFormatMixin, PanelPropertiesMixin, SequencerPlayerStudioStatusMixin],

	inject: {
		goLivePanel: {default: null}
	},

	props: ["params"],

	data: () => ({
		studioConnecting: false,
		studioDisconnecting: false,
		studioConnectStatus: {
			open: false,
			transitionType: null,
			steps: [
				{
					title: "Activating live player",
					status: 0, // Pending
					visible: true
				},
				{
					title: "Preparing live recording",
					status: 0, // Pending
					visible: true
				},
				{
					title: "Setting station to live mode",
					status: 0, // Pending
					visible: true
				},
				{
					title: "Connecting studio",
					status: 0, // Pending
					visible: true
				}
			]
		},
		loadingShow: false,
		ConferenceRoles: ConferenceRoles,
		TransitionType: TransitionType,
		transitionInterval: null,
		transitionTime: 0,
		microphoneAccessDialog: false,
		guestLinkDialog: false,
		ConfirmationType: ConfirmationType,
		heartbeatWorker: null,
		splitMasterStation: null
	}),

	computed: {
		panel() {
			if (this.goLivePanel) {
				return this.goLivePanel;
			}
			const {lg, xl} = this.$vuetify.display;
			return {lg, xl};
		},

		config() {
			return config;
		},

		organizationId() {
			return this.$store.getters.getOrgID();
		},

		isSplitMasterGoLiveDisabled() {
			return (
				this.selectedStation?.SyndicationType === SyndicationTypes.SplitMaster && config.ORIGIN === "ZETTACLOUD"
			);
		},

		currentShow() {
			return this.$store.getters.currentShow(this.selectedStationID);
		},

		currentTime() {
			return this.stationDateTime.clone().add(this.startLiveSequencerSnapshot?.TimeShiftLength ?? 0, "s");
		},

		controlStationFeature() {
			return (
				this.selectedStation &&
				this.$store.getters.isFeatureEnabled({
					object_id: this.selectedStation.StationID,
					reference: "zettacloud_live_console_control_station"
				})
			);
		},

		participateStationFeature() {
			return (
				this.selectedStation &&
				this.$store.getters.isFeatureEnabled({
					object_id: this.selectedStation?.StationID,
					reference: "zettacloud_live_console_participate_station"
				})
			);
		},

		monitorStationFeature() {
			return (
				this.selectedStation &&
				this.$store.getters.isFeatureEnabled({
					object_id: this.selectedStation.StationID,
					reference: "zettacloud_live_console_monitor_station"
				})
			);
		},

		guestLinkFeatureIsEnabled() {
			return (
				this.selectedStation &&
				this.$store.getters.isFeatureEnabled({
					object_id: this.selectedStation.StationID,
					reference: "zettacloud_live_console_enable_guest_invite"
				})
			);
		},

		disableButton() {
			return !(
				this.selectedStation &&
				this.sequencer &&
				this.studioSettings?.sip_connection_address?.length > 0 &&
				this.webRTCTokenFetched &&
				this.playersSnapshotsFetched
			);
		},

		webRTCToken() {
			return this.$store.getters.getWebRTCToken();
		},

		webRTCTokenFetched() {
			if (this.studioSettings?.sip_connection_address) {
				if (this.$store.getters.isWebRTCStudio()) {
					return !!this.webRTCToken;
				}
				return true;
			}
			return false;
		},

		ableToAskForWebRTCToken() {
			return this.$store.getters.isWebRTCStudio() && !!this.sequencer;
		},

		allPeersFormatted() {
			let text = [];
			const DMBA = this.player?.mixDelayActual;
			const DMBT = this.player?.mixDelayTarget;
			this.allPeers.forEach(peer => {
				const roleName = ConferenceRoles.getRoleName(peer.role);
				text.push(`${peer.studio} (${peer.type}) - ${roleName}: ${peer.status} (DMBA: ${DMBA}, DMBT: ${DMBT})`);
			});
			return _.uniq(text);
		},

		connectionStatus() {
			if (
				this.sequencerConnected ||
				((this.isSyndicationOriginated || this.isSplitMasterGoLiveDisabled) && this.connectedHost)
			) {
				return "Live Studio";
			} else if (this.sequencerWaitingForTransition || this.sequencerWaitingForEndOfSyndication) {
				if (
					!!this.startLiveSequencerSnapshot.MicStartDateTime &&
					moment.duration(this.transitionTime, "s").asHours() < 1
				) {
					return `Going Live in ${this.secondsToFormat(this.transitionTime, "mm:ss")}`;
				} else {
					return "Preparing Live";
				}
			} else if (this.sequencerWaitingForEndOfShow && this.userIsHost) {
				return "Preparing Live";
			} else if (
				!this.isSyndicatedShowEvent &&
				this.sequencerWaitingForEndOfShow &&
				this.endOfShowRemainingTime > 0
			) {
				return `Ending Live in ${this.secondsToFormat(this.endOfShowRemainingTime, "mm:ss")}`;
			} else if (this.sequencerWaitingForEndOfShow) {
				return "Ending Live";
			}
			return "Production Mode";
		},

		connectionErrorFormatted() {
			let errors = [];

			if (this.sequencerIsLive && !this.sequencerWaitingForEndOfSyndication && !this.playerIsLive) {
				errors.push("Player is not live.");
			}
			if (this.sequencerIsLive && !this.connectedHost) {
				errors.push("Studio connection not established.");
			}
			if (
				!this.sequencerIsLive &&
				!this.sequencerWaitingForEndOfShow &&
				!this.isSyndicationOriginated &&
				!this.isSplitMasterGoLiveDisabled &&
				this.connectedHost
			) {
				errors.push(
					"Something's gone terribly wrong. Please try to connect to and disconnect from the Studio."
				);
			}
			if (this.sequencerIsLive && !this.hostPeer) {
				errors.push('Sequencer is in "Live" state but there is not any host.');
			}
			if (this.userConnectionError) {
				errors.push(`SIP status: ${this.userConnectionError}`);
			}
			return errors;
		},

		waitingForTransition() {
			return this.sequencerWaitingForTransition && this.transitionTime === 0;
		},

		endOfShowRemainingTime() {
			let event = this.$store.getters
				.getSequencerItems(this.selectedStation.StationID)
				.find(item => item.StatusCode === EventStatus.Current);
			if (event) {
				while (event.ChildItems) {
					//Now Playing Event is a Spotblock or Local Break
					event = event.ChildItems.find(item => item.StatusCode === EventStatus.Current);
				}
				return this.$store.getters.getSequencerItemByEventID(this.selectedStation.StationID, event.EventID)
					?.RemainingTime;
			}
			return null;
		},

		isSyndicatedShowEvent() {
			if (this.selectedStation) {
				const eventID = this.$store.getters
					.getSequencerItems(this.selectedStation.StationID)
					.find(item => item.StatusCode === EventStatus.Current)?.EventID;
				const asset = this.$store.getters.getEventByID({
					stationID: this.selectedStation.StationID,
					eventID
				})?.Asset;

				if (asset) {
					return this.isValidURL(asset?.POIMarkup?.Path);
				}
			}
			return false;
		},

		studioConnectCurrentSteps() {
			const indexes = [];
			this.studioConnectStatus.steps
				.filter(s => s.visible)
				.forEach((s, i) => {
					if (s.status === 1) {
						indexes.push(i);
					}
				});
			return indexes;
		},

		systemInputDevices() {
			return this.$store.getters.systemInputDevices();
		},

		systemOutputDevices() {
			return this.$store.getters.systemOutputDevices();
		},

		missingDevices() {
			if (!_.isEmpty(this.studioSettings)) {
				const sysInputDevices = _.map(this.systemInputDevices, "name");
				const sysOutputDevices = _.map(this.systemOutputDevices, "name");

				const studioInputDevices = _.map(this.studioSettings.pc_audio_in, "name");
				const studioOutputDevices = _.concat(
					_.map(this.studioSettings.pc_audio_out, "name"),
					_.map(this.studioSettings.pc_audio_out_playout, "name"),
					this.studioSettings.pc_audio_out_audition ? [this.studioSettings.pc_audio_out_audition.name] : []
				);

				const inputDiff = _.difference(studioInputDevices, sysInputDevices);
				const outputDiff = _.difference(studioOutputDevices, sysOutputDevices);

				return inputDiff.concat(outputDiff);
			}
			return [];
		},

		transferHostMessage() {
			if (this.nonGuestParticipantPeers.length === 1) {
				return `Do you want to transfer control to the connected ${this.participantPeers[0].studio} studio?`;
			} else if (this.nonGuestParticipantPeers.length > 1) {
				return "Do you want to transfer control to another studio?";
			}
			return "";
		},

		transferHostOverlay() {
			return this.$store.getters.getAcceptTransferHostOverlay();
		},

		isNebulaAdapterOpen() {
			return this.$store.getters.isNebulaAdapterOpen();
		},

		sequencerMode() {
			return this.$store.getters.getSequencerMode(this.selectedStationID);
		},

		splitMasterStationID() {
			return this.selectedStation?.SplitMasterStationID;
		},

		studioIsAlreadyConnectedAsHost() {
			return this.$store.getters.isWebRTCStudio() && this.hostStudio === this.studioSettings?.reference;
		}
	},

	watch: {
		sequencerStartLiveState(state, previousState) {
			if (
				(!state && previousState) ||
				(state !== previousState && state === StartLiveStatus.WaitingForEndOfShow)
			) {
				this.studioDisconnecting = false;
			}
		},

		studioConnectStatus: {
			handler(studioConnectStatus) {
				if (studioConnectStatus.open) {
					if (studioConnectStatus.steps.every(s => !s.visible || s.status === 2)) {
						// If any step completed or hidden
						this.$worker.setTimeout(() => {
							this.studioConnectStatus.open = false;
						}, 1000);
					}
					if (studioConnectStatus.steps.some(s => s.visible && s.status === -1)) {
						// If any step failed, we need to remove the loaders from the buttons
						this.studioConnecting = false;
					}
				}
			},
			deep: true
		},

		userIsHost(isHost, previous) {
			if (this.isSyndicationOriginated || this.isSplitMasterGoLiveDisabled) {
				if (isHost && !previous) {
					this.studioConnecting = false;
				} else if (!isHost && !!previous) {
					this.studioDisconnecting = false;
				}
			}

			if (isHost && !previous) {
				this.studioConnecting = false;
				if (!_.isNil(this.studioSettings.delay_mixing_buffer_auto)) {
					this.setLivePlayerMixingBufferConfiguration({
						automatic: this.studioSettings?.delay_mixing_buffer_auto,
						adjustment: this.studioSettings?.delay_mixing_buffer
					});
				} else {
					this.setLivePlayerMixingBufferDepth(this.studioSettings?.delay_mixing_buffer);
				}

				if (this.isNebulaAdapterOpen) {
					this.$store.dispatch("adjustDucking", this.studioSettings.ducking_volume ?? 0);
				} else {
					const unwatch = this.$watch("isNebulaAdapterOpen", (currVal, oldVal) => {
						if (currVal && !oldVal) {
							this.$store.dispatch("adjustDucking", this.studioSettings.ducking_volume ?? 0);
							unwatch();
						}
					});
				}
			}
			this.$store.dispatch("livePlayerKickHostStudioSubscription", !!isHost);
		},

		userIsParticipant(isParticipant, previous) {
			if (isParticipant && !previous) {
				this.studioConnecting = false;
				this.$store.dispatch("livePlayerTransferHostSubscription", true);
			}
			if (!isParticipant && previous) {
				this.studioDisconnecting = false;
				this.$store.dispatch("livePlayerTransferHostSubscription", false);
			}
		},

		userIsGuest(isGuest, previous) {
			if (isGuest && !previous) {
				this.studioConnecting = false;
			}
			if (!isGuest && previous) {
				this.studioDisconnecting = false;
			}
		},

		ableToAskForWebRTCToken(readyToAsk) {
			if (readyToAsk && !this.webRTCToken) {
				this.$store.dispatch("getWebRTCToken").then(() => {
					this.reconnectStudio();
				});
			}
		},

		livePlayers: {
			handler(players) {
				const playerKeys = _.difference(
					_.map(players, "RealtimePlayerKey"),
					this.$store.getters.getLiveConferenceSnapshotPlayers()
				);
				playerKeys.forEach(playerKey => {
					this.$store.commit(
						//We set the player's conference snapshot to empty in order to avoid multiple requests to the same player
						"setLivePlayerConferenceSnapshot",
						{playerKey, conferenceSnapshot: {sipPeers: [], webRTCPeers: []}}
					);
					this.$store.dispatch("livePlayerConferenceRequest", playerKey);
				});
			},
			deep: true
		},

		missingDevices(newDevices) {
			if (newDevices.length > 0) {
				this.$store.commit("SHOW_PERSISTENT_NOTIFICATION", {
					title: "Missing Audio Devices",
					message: `The audio devices <br/> <b>${newDevices.toString()}</b> <br/> are not available`,
					type: "error"
				});
			} else {
				this.$store.commit("REMOVE_PERSISTENT_NOTIFICATION");
			}
		},

		"studioSettings.delay_mixing_buffer"(adjustment) {
			if (this.userIsHost) {
				if (!_.isNil(this.studioSettings.delay_mixing_buffer_auto)) {
					this.setLivePlayerMixingBufferConfiguration({
						automatic: this.studioSettings?.delay_mixing_buffer_auto,
						adjustment
					});
				} else {
					this.setLivePlayerMixingBufferDepth(adjustment);
				}
			}
		},

		"studioSettings.delay_mixing_buffer_auto"(automatic) {
			if (this.userIsHost) {
				this.setLivePlayerMixingBufferConfiguration({
					automatic,
					adjustment: this.studioSettings?.delay_mixing_buffer
				});
			}
		},

		sequencerConnected(isConnected) {
			isConnected ? this.userAnyRole && this.startSendingHeartbeat() : this.stopSendingHeartbeat();
		},

		userAnyRole(anyRole) {
			anyRole ? this.startSendingHeartbeat() : this.stopSendingHeartbeat();
		},

		sequencerMode(mode) {
			if (mode) {
				if (mode !== SequencerModes.Split) {
					this.splitMasterStation = null;
					this.getStationSchedulerEvents();
				} else {
					// While in Split Mode, in the Now Playing show, we will display the name of the Master station
					this.loadSplitMasterStation();
				}
			}
		},

		splitMasterStationID(id) {
			// While in Split Mode, in the Now Playing show, we will display the name of the Master station
			if (id && this.sequencerMode === SequencerModes.Split) {
				this.loadSplitMasterStation();
			}
		}
	},

	beforeUnmount() {
		if (this.currentShowTimeout) {
			this.$worker.clearTimeout(this.currentShowTimeout);
		}

		this.$store.dispatch("closeWebRTCConnection", true);
	},

	methods: {
		async studioConnect(role, transitionType) {
			const {state} = await navigator.permissions.query({name: "microphone"});
			if (state === "granted") {
				this.studioConnecting = true;
				this.studioDisconnecting = false;
				try {
					if (role === ConferenceRoles.Host) {
						if (
							!this.sequencerIsLive &&
							!this.isSyndicationOriginated &&
							!this.isSplitMasterGoLiveDisabled
						) {
							this.studioConnectStatus.steps.forEach(step => {
								step.status = 0;
								step.visible = true;
							});
							this.studioConnectStatus.transitionType =
								transitionType === TransitionType.Immediately.value
									? "immediately"
									: "on next transition";
							this.studioConnectStatus.open = true;

							const playerCreated = await this.createTempLivePlayer();
							if (playerCreated) {
								const recordingSession = await this.createRecordingSession();
								if (recordingSession) {
									await this.sequencerStartLiveRequest(recordingSession.asset_id, transitionType);
									await this.connectStudioRequest(role);
								}
							}
						} else {
							this.studioConnectStatus.steps.forEach((step, i) => {
								step.status = 0;
								if (this.isSyndicationOriginated || this.isSplitMasterGoLiveDisabled) {
									step.visible = i !== 1;
								} else {
									step.visible = i !== 1 && i !== 2;
								}
							});
							this.studioConnectStatus.transitionType = "station";
							this.studioConnectStatus.open = true;
							const playerCreated = await this.createTempLivePlayer();
							if (playerCreated) {
								if (this.isSyndicationOriginated || this.isSplitMasterGoLiveDisabled) {
									await this.updateStationOverrideSequencerSettings();
								}
								await this.connectStudioRequest(role);
							}
						}
					} else if (role === ConferenceRoles.Guest) {
						this.studioConnectStatus.steps.forEach((step, i) => {
							step.status = 0;
							step.visible = !(i === 1 || i === 2);
						});
						this.studioConnectStatus.transitionType = null;
						this.studioConnectStatus.open = true;

						const playerCreated = await this.createTempLivePlayer();
						if (playerCreated) {
							await this.connectStudioRequest(role);
						}
					} else {
						await this.connectStudioRequest(role);
					}
				} catch (error) {
					console.error("studioConnect error: ", error);
					this.studioConnecting = false;
				}
			} else {
				this.$store.commit("SHOW_NOTIFICATION_WITH_TYPE", {
					message:
						"You'll need to allow microphone access in order to have access to the PC Audio out devices.",
					action: {
						text: "Show me how",
						click: () => {
							this.microphoneAccessDialog = true;
						}
					}
				});
			}
		},

		studioDisconnect() {
			let role;
			if (this.userIsHost) {
				role = ConferenceRoles.Host;
			} else if (this.userIsParticipant) {
				role = ConferenceRoles.Participant;
				this.$store.commit("setAcceptTransferHostOverlay", false);
			} else if (this.userIsGuest) {
				role = ConferenceRoles.Guest;
			}

			this.studioDisconnecting = true;
			this.studioConnecting = false;
			if (
				(this.sequencerIsLive || this.sequencerWaitingForEndOfShow) &&
				this.userIsHost &&
				!this.isSyndicationOriginated &&
				!this.isSplitMasterGoLiveDisabled
			) {
				// todo check if this is correct
				const nowPlayingEvents = this.$store.getters
					.getSequencerItems()
					.filter(item => item.StatusCode === EventStatus.Current);

				const nowPlayingEventWithMaxDuration = Math.max(
					0,
					...nowPlayingEvents.map(item =>
						item.EventType === EventTypes.SpotBlock
							? Math.max(
									0,
									...item.ChildItems.filter(spot => spot.StatusCode === EventStatus.Current).map(
										spot => spot.DurationToSegue ?? 0
									)
								)
							: (item.DurationToSegue ?? 0)
					)
				);

				this.$store
					.dispatch("deleteRecordingSession", {
						recordingSession: this.startLiveSequencerSnapshot.AssetID,
						afterDuration: nowPlayingEventWithMaxDuration
					})
					.then(() => {
						this.$store.dispatch("sequencerEndLiveRequest");
						this.$store.dispatch("disconnectStudio", role);
					});
			} else {
				this.$store.dispatch("disconnectStudio", role);
			}

			if (JSON.parse(sessionStorage.getItem("liveState"))) {
				sessionStorage.removeItem("liveState");
			}
		},

		createTempLivePlayer() {
			this.studioConnectStatus.steps[0].status = 1; // Current
			return new Promise(resolve => {
				if (this.livePlayers.length === 0) {
					//Create a conditional watcher in order to handle the player creation
					const unwatch = this.$watch(
						"livePlayers",
						players => {
							if (players.length > 0) {
								this.studioConnectStatus.steps[0].status = 2; // Completed
								unwatch();
								resolve(true);
							}
						},
						{deep: true}
					);

					this.$store.dispatch("createTempLivePlayer").catch(() => {
						this.studioConnectStatus.steps[0].status = -1; // Error
						resolve(false);
					});
				} else {
					this.studioConnectStatus.steps[0].status = 2; // Completed
					resolve(true);
				}
			});
		},

		createRecordingSession() {
			this.studioConnectStatus.steps[1].status = 1; // Current step
			return this.$store
				.dispatch("createRecordingSession")
				.then(recordingSession => {
					if (recordingSession?.asset_id) {
						this.studioConnectStatus.steps[1].status = 2; // Completed
						return recordingSession;
					} else {
						this.studioConnectStatus.steps[1].status = -1; // Error
						return false;
					}
				})
				.catch(() => {
					this.studioConnectStatus.steps[1].status = -1; // Error
					return false;
				});
		},

		async updateStationOverrideSequencerSettings() {
			this.studioConnectStatus.steps[2].status = 1; // Current
			return await this.$store
				.dispatch("updateStation", {
					stationID: this.selectedStationID,
					payload: {
						OverrideSequencerSettings: {
							PotentiometerCount: this.studioSettings.pots_count
						}
					}
				})
				.then(response => {
					if (response.status === 200) {
						this.studioConnectStatus.steps[2].status = 2; // Completed
					} else {
						this.studioConnectStatus.steps[2].status = -1; // Error
					}
				})
				.catch(() => {
					this.studioConnectStatus.steps[2].status = -1; // Error
				});
		},

		sequencerStartLiveRequest(assetId, transitionType) {
			this.studioConnectStatus.steps[2].status = 1; // Current step
			return this.$store
				.dispatch("sequencerStartLiveRequest", {assetId, transitionType})
				.then(() => {
					this.studioConnectStatus.steps[2].status = 2; // Completed
				})
				.catch(error => {
					this.studioConnectStatus.steps[2].status = -1; // Error
					return Promise.reject(error);
				});
		},

		connectStudioRequest(role) {
			this.studioConnectStatus.steps[3].status = 1; // Current step
			return this.$store
				.dispatch("connectStudio", role)
				.then(() => {
					this.studioConnectStatus.steps[3].status = 2; // Completed
				})
				.catch(error => {
					this.studioConnectStatus.steps[3].status = -1; // Error
					return Promise.reject(error);
				});
		},

		async reconnectStudio() {
			const liveState = JSON.parse(sessionStorage.getItem("liveState"));
			if (liveState?.station === this.selectedStationID && liveState?.studio === this.studioSettings.reference) {
				try {
					switch (liveState.role) {
						case ConferenceRoles.Host:
							if (
								(!this.connectedHost || this.userIsHost) &&
								(this.sequencerIsLive ||
									this.isSyndicationOriginated ||
									this.isSplitMasterGoLiveDisabled)
							) {
								await this.connectStudioRequest(liveState.role);
								this.$store.commit("setMicOnOff", liveState.micOn);
								if (liveState.midiIsOpen) {
									emitter.emit("midiWindowOpen");
								}
							}
							break;
						case ConferenceRoles.Participant:
							if (
								this.connectedHost &&
								(this.sequencerIsLive ||
									this.isSyndicationOriginated ||
									this.isSplitMasterGoLiveDisabled)
							) {
								await this.connectStudioRequest(liveState.role);
								this.$store.commit("setMicOnOff", liveState.micOn);
							}
							break;
						case ConferenceRoles.Guest:
							await this.connectStudioRequest(liveState.role);
							break;
					}
				} catch (error) {
					console.error("reconnectStudio error: ", error);
				}
			}
		},

		transferHost(participant) {
			this.studioDisconnecting = true;
			this.studioConnecting = false;

			this.$store.dispatch("disconnectStudio", ConferenceRoles.Host);

			const unwatch = this.$watch("userIsHost", isHost => {
				if (!isHost) {
					if (participant.type === "SIP") {
						this.$store.dispatch("livePlayerTransferHost", participant);
					} else if (participant.type === "webRTC") {
						this.$store.dispatch("livePlayerTransferHostWebRTC", participant);
					}

					if (JSON.parse(sessionStorage.getItem("liveState"))) {
						sessionStorage.removeItem("liveState");
					}

					unwatch();
				}
			});
		},

		acceptTransferHost() {
			this.$store.dispatch("transferHostWebRTC");
			this.$store.commit("setAcceptTransferHostOverlay", false);
		},

		takeOverStudioConnection() {
			this.studioConnecting = true;
			this.$store.dispatch("livePlayerKickHostStudioWebRTC", this.hostPeer);
			return this.$store.dispatch("connectStudio", ConferenceRoles.Host).catch(error => {
				this.studioConnecting = false;
				console.error("takeOverStudioConnection error: ", error);
			});
		},

		loadMore(isIntersecting) {
			if (isIntersecting) {
				this.loadStations(this.offset + this.limit);
			}
		},

		isValidURL(url) {
			try {
				return Boolean(new URL(url));
			} catch (e) {
				return false;
			}
		},

		setLivePlayerMixingBufferConfiguration({automatic, adjustment}) {
			if (!_.isNil(automatic) || !_.isNil(adjustment)) {
				this.$store.dispatch("livePlayerSetMixingBufferConfiguration", {
					playerKey: this.userPeer.playerKey,
					automatic,
					adjustment
				});
			}
		},

		// todo: backward compatibility function. Remove on next astrum release
		setLivePlayerMixingBufferDepth(depth) {
			if (!_.isNil(depth)) {
				this.$store.dispatch("livePlayerSetMixingBufferDepth", {playerKey: this.userPeer.playerKey, depth});
			}
		},

		startSendingHeartbeat() {
			if (this.heartbeatWorker) {
				this.$worker.clearInterval(this.heartbeatWorker);
			}
			this.sendHeartbeat();
			this.heartbeatWorker = this.$worker.setInterval(this.sendHeartbeat, 60000);
		},

		stopSendingHeartbeat() {
			if (!this.sequencerConnected && !this.userAnyRole && this.heartbeatWorker) {
				this.$worker.clearInterval(this.heartbeatWorker);
				this.sendHeartbeat();
			}
		},

		sendHeartbeat() {
			this.$store.dispatch("sendHeartbeat", {
				mode: this.isSyndicationOriginated || this.isSplitMasterGoLiveDisabled ? 2 : +this.sequencerConnected, //cast boolean to int (false -> 0, true -> 1)
				role: this.userPeer?.role ?? -1,
				studio: this.studioSettings.reference,
				user_name: this.$store.getters.getUserFullName(),
				tm: this.stationDateTime.valueOf(),
				asset_id: this.startLiveSequencerSnapshot?.AssetID ?? null,
				connection_type: this.studioSettings.sip_connection_address.includes("webRTC") ? 2 : 1
			});
		},

		getStationSchedulerEvents() {
			this.loadingShow = true;
			this.$store.commit("resetCurrentShow", this.selectedStation.StationID);
			if (this.currentShowTimeout) {
				this.$worker.clearTimeout(this.currentShowTimeout);
				this.currentShowTimeout = null;
			}

			const stationDateTime = moment.tz(this.selectedStation.StationTimeZone);
			const period = {
				from: stationDateTime.clone().subtract(1, "days").startOf("day").valueOf(),
				to: stationDateTime.clone().add(1, "days").endOf("day").valueOf()
			};

			this.$store
				.dispatch("getStationSchedulerEvents", {period, stationID: this.selectedStation.StationID})
				.then(events => {
					let nowPlayingSchedulerEvent = events.find(event => {
						return (
							event.priority !== PriorityLanes.Split.priority && // not Split show event
							!event.cancelled &&
							moment().isBetween(event.start, event.start + event.duration * 1000, undefined, "[]")
						);
					});

					if (nowPlayingSchedulerEvent) {
						this.$store
							.dispatch("getShow", {
								stationID: this.selectedStation.StationID,
								showID: nowPlayingSchedulerEvent.object_id,
								current: true
							})
							.finally(() => {
								this.loadingShow = false;
							});

						let millisecondsUtilEventEnds = moment(nowPlayingSchedulerEvent.start)
							.add(nowPlayingSchedulerEvent.duration, "minutes")
							.diff(moment());

						const oneHour = 60 * 60 * 1000;
						this.currentShowTimeout = this.$worker.setTimeout(
							this.getStationSchedulerEvents,
							millisecondsUtilEventEnds < oneHour ? millisecondsUtilEventEnds : oneHour
						);
					} else {
						this.loadingShow = false;
						if (this.currentShowTimeout) {
							this.$worker.clearTimeout(this.currentShowTimeout);
							this.currentShowTimeout = null;
						}
					}
				});
		},

		loadSplitMasterStation() {
			if (this.splitMasterStationID) {
				this.splitMasterStation = this.$store.getters.getStationById(this.splitMasterStationID);
				if (!this.splitMasterStation) {
					this.$store
						.dispatch("getStation", {StationID: this.splitMasterStationID})
						.then(station => {
							this.splitMasterStation = {
								name: station?.StationName ?? this.splitMasterStationID
							};
						})
						.catch(() => (this.splitMasterStation = {name: this.splitMasterStationID}));
				}
			}
		}
	}
};
</script>

<style lang="scss" scoped>
.show-name {
	display: -webkit-box;
	overflow: hidden;
	text-overflow: ellipsis;
	-webkit-box-orient: vertical;
	-webkit-line-clamp: 2;
}

.status {
	background: $status-neutral !important;
	font-size: 1rem;
	height: 32px;
	text-transform: uppercase;

	&.studio-connected {
		background: $status-connected !important;

		&.warning {
			background: $status-warning !important;
		}

		&.error {
			background: $status-error !important;
		}
	}

	&.transition-production-mode {
		background-color: $ihm-yellow-warning !important;
	}
}

#alert-icon {
	animation: blinker 1s linear infinite;
}

@keyframes blinker {
	50% {
		opacity: 0;
	}
}

.v-card {
	&.v-theme--dark {
		.current-day {
			color: $ihm-white;
		}
	}
}

:deep(.v-stepper-header) {
	box-shadow: unset;

	.v-stepper-item .v-avatar {
		height: 20px !important;
		width: 20px !important;
	}
}
</style>
